// Carrito de accesorios en un cajón lateral: líneas, cobro y total. La venta va
// sin cliente. «Cobrar» dice qué falta mientras no se pueda; vacío no se ofrece.
import { Check, Minus, Plus, X } from 'lucide-react';
import { Button } from '@/kit';
import { cn } from '@/lib/cn';
import { SideDrawer } from '@/shared/patterns/SideDrawer';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { maxQtyFor } from '@/domains/sales/wizard/cartLines';
import { cartCountLabel, type CartSummary } from '../lib/accessoriesCart';
import { PaymentListForm } from '@/domains/sales/wizard/components/PaymentListForm';
import type { PaymentDraft } from '@/domains/sales/wizard/salePayments';
import { isTrackedPiece, type CartIdentifiers } from '../lib/accessoriesCheckout';
import { PieceSerialInputs } from './PieceSerialInputs';

const SECTION = 'border-t border-border py-3 first:border-t-0 first:pt-0';
const SECTION_TITLE =
    'mb-1.5 text-[9.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground';
export const QTY_BUTTON =
    'flex size-[22px] items-center justify-center rounded border border-border bg-card text-foreground hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40';

export function AccessoriesCartDrawer({
    open,
    onClose,
    summary,
    onChangeQty,
    identifiers,
    onIdentifiersChange,
    onRemove,
    onClear,
    payments,
    onPaymentsChange,
    blocker,
    onCharge,
    charging,
    saleError,
}: {
    open: boolean;
    onClose: () => void;
    summary: CartSummary;
    onChangeQty: (sku: string, delta: number) => void;
    identifiers: CartIdentifiers;
    onIdentifiersChange: (sku: string, next: string[]) => void;
    onRemove: (sku: string) => void;
    onClear: () => void;
    payments: readonly PaymentDraft[];
    onPaymentsChange: (next: readonly PaymentDraft[]) => void;
    /** Lo que falta para cobrar; `null` si ya se puede. */
    blocker: string | null;
    onCharge: () => void;
    charging: boolean;
    /** Rechazo del último intento de venta, con su acción ya aplicada. */
    saleError: string | null;
}) {
    return (
        <SideDrawer
            open={open}
            onClose={onClose}
            className="w-[400px]"
            title={`Carrito (${String(summary.unitsCount)})`}
            footer={
                <>
                    {saleError && (
                        <p
                            role="alert"
                            className="mb-2 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-1.5 text-[11.5px] leading-relaxed text-destructive"
                        >
                            {saleError}
                        </p>
                    )}
                    <Button
                        className="w-full"
                        disabled={blocker !== null}
                        isLoading={charging}
                        aria-describedby={blocker ? 'accessories-checkout-blocker' : undefined}
                        leftIcon={<Check className="size-4" aria-hidden />}
                        onClick={onCharge}
                    >
                        Cobrar {formatMoneyCents(summary.subtotalCents)}
                    </Button>
                    {blocker && (
                        <p
                            id="accessories-checkout-blocker"
                            className="mt-1.5 text-center text-[10.5px] italic text-muted-foreground"
                        >
                            {blocker}
                        </p>
                    )}
                </>
            }
        >
            <section aria-label="Carrito">
                <div className={SECTION}>
                    <div className="mb-1 flex items-center justify-between">
                        <div className={cn(SECTION_TITLE, 'mb-0')}>Artículos</div>
                        <button
                            type="button"
                            onClick={onClear}
                            className="text-[10.5px] text-muted-foreground underline hover:text-danger"
                        >
                            Vaciar
                        </button>
                    </div>
                    <ul className="divide-y divide-border/50">
                        {summary.lines.map(({ item, qty, lineTotalCents }) => {
                            const name = `${item.brand} ${item.model}`.trim();
                            return (
                                <li
                                    key={item.sku}
                                    className="grid grid-cols-[1fr_auto] items-center gap-2 py-2"
                                >
                                    <div className="min-w-0">
                                        <div
                                            className="truncate text-[12px] font-semibold"
                                            title={name}
                                        >
                                            {name}
                                        </div>
                                        <div className="text-[10.5px] tabular-nums text-muted-foreground">
                                            {formatMoneyCents(item.cashPriceCents)} × {qty} ={' '}
                                            <strong className="text-foreground">
                                                {formatMoneyCents(lineTotalCents)}
                                            </strong>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button
                                            type="button"
                                            aria-label={`Quitar una unidad de ${name}`}
                                            onClick={() => {
                                                onChangeQty(item.sku, -1);
                                            }}
                                            className={QTY_BUTTON}
                                        >
                                            <Minus className="size-3" aria-hidden />
                                        </button>
                                        <span className="min-w-[22px] text-center text-xs font-bold tabular-nums">
                                            {qty}
                                        </span>
                                        <button
                                            type="button"
                                            aria-label={`Agregar una unidad de ${name}`}
                                            disabled={qty >= maxQtyFor(item)}
                                            onClick={() => {
                                                onChangeQty(item.sku, 1);
                                            }}
                                            className={QTY_BUTTON}
                                        >
                                            <Plus className="size-3" aria-hidden />
                                        </button>
                                        <button
                                            type="button"
                                            aria-label={`Quitar ${name}`}
                                            title="Quitar"
                                            onClick={() => {
                                                onRemove(item.sku);
                                            }}
                                            className="ml-1 flex size-[22px] items-center justify-center rounded text-muted-foreground hover:bg-danger/15 hover:text-danger"
                                        >
                                            <X className="size-3.5" aria-hidden />
                                        </button>
                                    </div>
                                    {isTrackedPiece(item) && (
                                        <PieceSerialInputs
                                            name={name}
                                            qty={qty}
                                            values={identifiers[item.sku] ?? []}
                                            onChange={(next) => {
                                                onIdentifiersChange(item.sku, next);
                                            }}
                                        />
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>

                <div className={cn(SECTION, 'flex flex-col gap-3')}>
                    <PaymentListForm
                        layout="stack"
                        drafts={payments}
                        onChange={onPaymentsChange}
                        totalCents={summary.subtotalCents}
                    />
                </div>

                <div className={SECTION}>
                    <div className="flex items-baseline justify-between text-[11px] text-muted-foreground">
                        <span>{cartCountLabel(summary.itemsCount, summary.unitsCount)}</span>
                    </div>
                    <div className="mt-1 flex items-baseline justify-between text-[15px] font-bold tabular-nums">
                        <span>Total a cobrar</span>
                        <span>{formatMoneyCents(summary.subtotalCents)}</span>
                    </div>
                </div>
            </section>
        </SideDrawer>
    );
}
